import React, { useContext, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "components/Graphs/Dialogue";
import { GPUInstanceType } from "types/InstanceTypes";
import { allPublicCloudsName } from "components/Environment/Create/utils";
import { OrgContext } from "contexts/OrgContext";
import { InstanceTypesContext } from "contexts/InstanceTypesContext";
import { ContainerSelected } from "components/Environment/shared/BuildTypes";
import FilterBar from "components/Environment/Create/ComputeSelector/Advanced/FilterBar";
import ComputeSelector from "components/Environment/Create/ComputeSelector";
import { AnimatePresence } from "framer-motion";
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import { Button } from "components/Graphs/Button";
import { getPrimaryInstanceCloudCred } from "utils/cloudCreds";
import { isStorageValueValid } from "utils/instanceTypes";

const DEFAULT_CONTAINER: ContainerSelected = {
  vmBuild: {
    forceJupyterInstall: false,
  },
};

interface EditComputeModalProps {
  intendedInstanceType: string | null;
  currentInstanceType?: GPUInstanceType | null;
  show: boolean;
  setShow: (show: boolean) => void;
  onSuccess: (selectedInstanceType: GPUInstanceType, storage: string) => void;
  imageId?: string | null;
  lockedCloudProvider?: string | null;
}

type EditComputeModalContentProps = Omit<
  EditComputeModalProps,
  "show" | "setShow"
> & {
  onClose: () => void;
};

const getInitialStorage = (instanceType?: GPUInstanceType) =>
  getPrimaryInstanceCloudCred(instanceType)?.id === "dgxc-gcp-1"
    ? "512"
    : "256";

const EditComputeModalContent: React.FC<EditComputeModalContentProps> = ({
  intendedInstanceType,
  currentInstanceType,
  onSuccess,
  onClose,
  imageId,
  lockedCloudProvider,
}) => {
  const orgContext = useContext(OrgContext);
  const instanceTypesContext = useContext(InstanceTypesContext);

  const [selectedInstanceType, setSelectedInstanceType] = useState<
    GPUInstanceType | undefined
  >(undefined);
  const [storage, setStorage] = useState<string>("256");

  const selectedCloudName = allPublicCloudsName;

  const [displayGPUInstances, setDisplayGPUInstances] = useState<
    GPUInstanceType[] | undefined
  >(undefined);
  const [displayCPUInstances, setDisplayCPUInstances] = useState<
    GPUInstanceType[] | undefined
  >(undefined);
  const [persistentOnly, setPersistentOnly] = useState<boolean>(false);
  const [flexiblePortControlOnly, setFlexiblePortControlOnly] =
    useState<boolean>(false);
  const [flexibleStorageOnly, setFlexibleStorageOnly] =
    useState<boolean>(false);
  const initialSelectedChip = currentInstanceType?.supported_gpus?.[0]?.name
    ? currentInstanceType.supported_gpus[0].name
    : currentInstanceType
      ? "CPU"
      : undefined;

  const handleInstanceSelection = (instanceType?: GPUInstanceType) => {
    setSelectedInstanceType(instanceType);
    if (instanceType) {
      setStorage(getInitialStorage(instanceType));
    }
  };

  const filterForValidInstances = useMemo(() => {
    return instanceTypesContext.GPUInstancesAvailable.filter(
      (gpu) => getPrimaryInstanceCloudCred(gpu)?.id !== "dgxc-gcp-1",
    );
  }, [instanceTypesContext.GPUInstancesAvailable]);

  const storageValid = isStorageValueValid(storage, selectedInstanceType);

  return (
    <DialogContent
      className="!max-w-7xl max-h-[90vh] min-h-[500px] flex flex-col overflow-hidden w-full"
      showCloseButton={false}
    >
      <DialogHeader className="flex-shrink-0">
        <div className="flex flex-col gap-4">
          <div>
            <DialogTitle className="text-primary text-xl font-semibold">
              Edit Compute
            </DialogTitle>
            <DialogDescription className="text-secondary">
              Configure this launchable&apos;s compute resources.
            </DialogDescription>
          </div>
          <div className="w-full">
            <FilterBar
              allGPUInstances={filterForValidInstances}
              allCPUInstances={instanceTypesContext.CPUInstancesAvailable}
              setDisplayGPUInstances={setDisplayGPUInstances}
              setDisplayCPUInstances={setDisplayCPUInstances}
              containerSelected={DEFAULT_CONTAINER}
              onFilterStateChange={(
                persistent,
                flexiblePorts,
                flexibleStorage,
              ) => {
                setPersistentOnly(persistent);
                setFlexiblePortControlOnly(flexiblePorts);
                setFlexibleStorageOnly(flexibleStorage);
              }}
              lockedProvider={
                imageId && lockedCloudProvider ? lockedCloudProvider : undefined
              }
            />
          </div>
        </div>
      </DialogHeader>

      <hr className="w-[100%] my-3 dark:border dark:border-zinc-800 flex-shrink-0" />

      <div className="flex flex-col flex-1 overflow-y-auto min-h-0 px-[1.5%] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-zinc-600 [&::-webkit-scrollbar-thumb:hover]:bg-zinc-400">
        <AnimatePresence initial={false}>
          <ComputeSelector
            allGPUInstances={filterForValidInstances}
            isLoadingInstances={instanceTypesContext.isInstancesLoading}
            displayGPUInstances={displayGPUInstances}
            selectedInstance={selectedInstanceType}
            setSelectedInstance={handleInstanceSelection}
            setStorage={setStorage}
            storage={storage}
            containerSelected={DEFAULT_CONTAINER}
            selectedCloudName={selectedCloudName}
            hideDgxc
            persistentOnly={persistentOnly}
            flexiblePortControlOnly={flexiblePortControlOnly}
            flexibleStorageOnly={flexibleStorageOnly}
            syncGpuSelectionToUrl={false}
            initialSelectedChip={initialSelectedChip}
            cpuInstances={
              displayCPUInstances ?? instanceTypesContext.CPUInstancesAvailable
            }
          />
        </AnimatePresence>
      </div>

      {selectedInstanceType && (
        <DialogFooter className="w-full mt-4 pt-4 flex flex-row justify-between flex-shrink-0 border-t border-zinc-800">
          {intendedInstanceType !== selectedInstanceType.type ? (
            <div className="flex flex-row justify-between">
              <div className="flex flex-row items-center border border-red-500 rounded-md p-2">
                <ExclamationTriangleIcon className="h-4 w-4 mr-1 text-red-500" />
                <span className="text-sm text-red-500 font-bold">
                  This instance type is different from what the author
                  originally configured. Unexpected behavior could occur.
                </span>
              </div>
            </div>
          ) : (
            <div />
          )}
          <div className="flex flex-row gap-2">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!storageValid}
              onClick={() => {
                onClose();
                onSuccess(selectedInstanceType, storage);
              }}
            >
              Save Compute
            </Button>
          </div>
        </DialogFooter>
      )}
    </DialogContent>
  );
};

export const EditComputeModal: React.FC<EditComputeModalProps> = ({
  show,
  setShow,
  ...contentProps
}) => {
  const handleClose = () => setShow(false);

  return (
    <Dialog open={show} onOpenChange={setShow}>
      {show && (
        <EditComputeModalContent {...contentProps} onClose={handleClose} />
      )}
    </Dialog>
  );
};
